import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Link,
  InputLabel,
} from '@mui/material';

// COMPNENTS
import CustomModal from '../../../src/components/common/CustomModal';
import Paginator from '../../../src/components/common/Paginator';

// CONFIG
import config from '../../Config';

// REQUESTS
import useTemplateRequest from '../../requests/TemplateRequest';

// MESSAGES
import TemplateFormattedMessages from '../../messages/websms/TemplateFormattedMessages';

const TemplateTable = ({
  list,
  handleAddNewClick,
  errorHandler,
  setTemplateData,
  setDisplayTemplatePopup,
  type,
  setData,
  accessLevel,
  accessType,
  setModalType,
}) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(true);
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [allChecked, setAllChecked] = useState(false);
  const [checkedTemplates, setCheckedTemplates] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const { deleteTemplate, getTemplates } = useTemplateRequest();

  /*
    Handles the change event when clicking a pagination number
  */
  const changePageEvent = (page) => {
    setPageNumber(page);
  };

  /*
    Handles changes in the form
  */
  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
    setPageNumber(1);
  };

  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  const deleteSelected = async () => {
    setDeleteDisabled(true);
    for (const template of checkedTemplates) {
      await deleteTemplate(template, type, errorHandler);
    }
    const updatedData = await getTemplates();
    setData(updatedData);
    setDeleteModalOpen(false);
    setDeleteDisabled(false);
  };

  /*
    Handles global template search
  */
  const handleSearchFilter = () => {
    if (searchValue) {
      return list.filter((contact) => {
        return `${contact.template_name}`
          .toLowerCase()
          .includes(searchValue.toLowerCase());
      });
    }
    return list;
  };

  const filteredList = handleSearchFilter();

  const handleTemplateCheckboxClick = (id) => {
    const templateId = id;
    setCheckedTemplates((prevCheckedTemplates) =>
      prevCheckedTemplates.includes(templateId)
        ? prevCheckedTemplates.filter((id) => id !== templateId)
        : [...prevCheckedTemplates, templateId]
    );
  };

  const handleTemplateClick = (template) => {
    setModalType('edit');
    setTemplateData({
      template_sms_id: template.template_sms_id,
      template_name: template.template_name,
      template_text: template.template_text,
      template_type: template.template_type,
    });
    setDisplayTemplatePopup(true);
  };

  const renderTemplatesTable = () => {
    if (isSearching && !list) {
      return (
        <TableRow>
          <TableCell colSpan={4} sx={{ paddingY: 2 }}>
            <Typography>Searcing...</Typography>
          </TableCell>
        </TableRow>
      );
    } else if (list.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={4} sx={{ paddingY: 2 }}>
            <Typography>No results</Typography>
          </TableCell>
        </TableRow>
      );
    } else {
      return filteredList
        .slice(
          (pageNumber - 1) * pageLength,
          (pageNumber - 1) * pageLength + pageLength
        )
        .map((template, index) => (
          <TableRow key={index}>
            <TableCell>
              <Link
                color="secondary"
                onClick={() => handleTemplateClick(template)}
                tabIndex={0}
              >
                {template.template_name}
              </Link>
            </TableCell>
            <TableCell>
              <Box
                sx={{
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {template.template_text}
              </Box>
            </TableCell>
            <TableCell>{template.template_type}</TableCell>
            <TableCell align="right">
              <Checkbox
                sx={{ padding: 0 }}
                checked={checkedTemplates.includes(template.template_sms_id)}
                onChange={() =>
                  handleTemplateCheckboxClick(template.template_sms_id)
                }
                disabled={accessLevel <= '0' || accessType === '0'}
                name="templateCheckbox"
              />
            </TableCell>
          </TableRow>
        ));
    }
  };

  const handleAllCheckedChange = () => {
    const currentPageTemplates = list
      .slice(
        (pageNumber - 1) * pageLength,
        (pageNumber - 1) * pageLength + pageLength
      )
      .map((template) => template.template_sms_id);

    if (allChecked) {
      // Uncheck only the templates on the current page
      setCheckedTemplates((prevCheckedTemplates) =>
        prevCheckedTemplates.filter((id) => !currentPageTemplates.includes(id))
      );
    } else {
      // Check only the templates on the current page, keeping existing selections
      setCheckedTemplates((prevCheckedTemplates) => [
        ...new Set([...prevCheckedTemplates, ...currentPageTemplates]),
      ]);
    }

    setAllChecked(!allChecked);
  };

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12 }}>
        <Box
          sx={{
            background: 'white',
            borderRadius: '0 0 20px 20px',
            paddingX: 4,
            paddingY: 2,
          }}
        >
          <InputLabel htmlFor="contactSearch">
            {type === 'Global'
              ? TemplateFormattedMessages.searchGlobalTemplates
              : TemplateFormattedMessages.searchPersonalTemplates}
          </InputLabel>

          <TextField
            id="contactSearch"
            name="contactSearch"
            variant="outlined"
            value={searchValue}
            onChange={handleSearchChange}
            fullWidth
            size="small"
          />
        </Box>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: 1,
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={handleAddNewClick}
          >
            {TemplateFormattedMessages.createNewButton}
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            onClick={
              checkedTemplates.length > 0
                ? () => setDeleteModalOpen(true)
                : null
            }
            disabled={checkedTemplates.length === 0}
          >
            {TemplateFormattedMessages.deleteSelected}
          </Button>
        </Stack>
        <TableContainer
          sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
        >
          <Table variant="rounded">
            <TableHead sx={{ whiteSpace: 'nowrap' }}>
              <TableRow>
                <TableCell width="25%">
                  {TemplateFormattedMessages.templateName}
                </TableCell>

                <TableCell width="50%">
                  {TemplateFormattedMessages.contentText}
                </TableCell>

                <TableCell width="15%">
                  {TemplateFormattedMessages.templateType}
                </TableCell>

                <TableCell width="10%" align="right">
                  <Checkbox
                    sx={{ padding: 0 }}
                    checked={allChecked}
                    onChange={(e) => handleAllCheckedChange(e)}
                    name="selectAllTemplates"
                    disabled={accessLevel <= '0' || accessType === '0'}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderTemplatesTable()}</TableBody>
          </Table>
        </TableContainer>

        <Paginator
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          itemCount={filteredList.length ?? 0}
          changeEvent={changePageEvent}
          pageLength={pageLength}
          editFunction={(e) => changePageLength(e)}
          showTotal={'Found'}
          toolTip={'Maximum of 4,000 records shown'}
        />
      </Grid>

      <CustomModal
        setOpen={setDeleteModalOpen}
        open={deleteModalOpen}
        title={'Confirm Delete'}
        width={550}
      >
        <Typography>
          Are you sure you want to delete {checkedTemplates.length} templates?
        </Typography>

        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            marginTop: 2,
            gap: 2,
          }}
        >
          <Button
            variant="link"
            onClick={() => setDeleteModalOpen(false)}
            color="secondary"
          >
            {TemplateFormattedMessages.cancelText}
          </Button>

          <Button
            variant="contained"
            disabled={deleteDisabled}
            color="secondary"
            onClick={deleteSelected}
          >
            {TemplateFormattedMessages.deleteText}
          </Button>
        </Stack>
      </CustomModal>
    </Grid>
  );
};

export default TemplateTable;
